import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { GlassmorphismContainer } from "../styled/shared/Glassmorphism";
import {
  useAreaGenerateStore,
  useCesiumStore,
  useCustomAlertStore,
  usePathStore,
} from "../../Store/store";
import { GetPolygonModeId, PostPolygonMode } from "../../api/cesiumAPI";
import { PostAreaMode } from "../../api/potreeAPI";
import { GetAreaModeId } from "../../api/potreeAPI";
import { useLocation } from "react-router-dom";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  @media (max-width: 600px) {
    backdrop-filter: blur(2px);
  }
`;

const ModalContainer = styled(GlassmorphismContainer)`
  width: 380px;
  min-height: 300px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  padding: 24px 28px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  @media (max-width: 600px) {
    width: 98%;
    min-height: 200px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
  margin-bottom: 20px;
  @media (max-width: 600px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  @media (max-width: 600px) {
    gap: 10px;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 5px;
  @media (max-width: 600px) {
    font-size: 12px;
    margin-bottom: 3px;
  }
`;

const Input = styled.input`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
  @media (max-width: 600px) {
    height: 30px;
    font-size: 12px;
  }
`;

const ColorDisplay = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: ${(props) => props.color || "#3B82F6"};
  @media (max-width: 600px) {
    width: 20px;
    height: 20px;
  }
`;

const ColorPickerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 600px) {
    gap: 6px;
  }
`;

const ColorPicker = styled.input`
  width: 50px;
  height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
  @media (max-width: 600px) {
    width: 40px;
    height: 30px;
    font-size: 12px;
  }
`;

const ColorInput = styled.input`
  flex: 1;
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;
  font-size: 14px;
  font-family: "Courier New", monospace;

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  @media (max-width: 600px) {
    height: 30px;
    font-size: 12px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
  @media (max-width: 600px) {
    gap: 5px;
    margin-top: 5px;
  }
`;

const Button = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &.primary {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-1px);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
  @media (max-width: 600px) {
    padding: 5px 10px;
    font-size: 12px;
    min-width: 60px;
  }
`;

export default function AddAreaModeModal() {
  const location = useLocation();
  const {
    newAreaName,
    setNewAreaName,
    areaColor,
    setAreaColor,
    selectedAreaType,
    isAddAreaModeOpen,
    setIsAddAreaModeOpen,
  } = useAreaGenerateStore();
  const { setPolygonModeIdList } = useCesiumStore();
  const { setAreaModeIdList } = usePathStore();
  const { showSuccessAlert, showErrorAlert, showInfoAlert } =
    useCustomAlertStore();
  const inputRef = useRef(null);

  const handleConfirm = useCallback(async () => {
    try {
      const data = {
        newAreaName,
        areaColor,
      };
      let response;
      if (location.pathname.includes("/potree")) {
        response = await PostAreaMode(data);
      } else {
        response = await PostPolygonMode(data);
      }

      if (location.pathname.includes("/potree")) {
        const areaModeIdList = await GetAreaModeId();

        setAreaModeIdList(areaModeIdList);
      } else {
        const polygonModeIdList = await GetPolygonModeId();

        setPolygonModeIdList(polygonModeIdList);
      }

      showSuccessAlert("영역 생성 성공", "영역이 생성되었습니다.");
      setIsAddAreaModeOpen(false);
    } catch (error) {
      showErrorAlert("영역 생성 실패", "영역이 생성되지 않았습니다.");
    }
  }, [newAreaName, areaColor, setIsAddAreaModeOpen]);

  const handleCancel = useCallback(() => {
    setIsAddAreaModeOpen(false);
  }, [setIsAddAreaModeOpen]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  useEffect(() => {
    if (!isAddAreaModeOpen) return;
    const handler = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isAddAreaModeOpen, handleConfirm, handleCancel]);

  useEffect(() => {
    if (isAddAreaModeOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isAddAreaModeOpen]);

  if (!isAddAreaModeOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <Title>새로운 영역 설정</Title>

        <FormContainer>
          <Label htmlFor="newAreaNameInput">새로운 영역 이름</Label>
          <Input
            ref={inputRef}
            id="newAreaNameInput"
            type="text"
            placeholder="새로운 영역 이름"
            value={newAreaName}
            onChange={(e) => setNewAreaName(e.target.value)}
            required={selectedAreaType === "newAreaGenerate"}
          />

          {/* 영역 색상 선택 */}
          <Label htmlFor="areaColorInput">영역 색상</Label>
          <ColorPickerContainer>
            <ColorPicker
              type="color"
              value={areaColor}
              onChange={(e) => setAreaColor(e.target.value)}
            />
            <ColorDisplay color={areaColor} />
            <ColorInput
              type="text"
              value={areaColor}
              onChange={(e) => setAreaColor(e.target.value)}
              placeholder="#3B82F6"
            />
          </ColorPickerContainer>
        </FormContainer>

        <ButtonRow>
          <Button className="secondary" onClick={handleCancel}>
            취소
          </Button>
          <Button className="primary" onClick={handleConfirm}>
            확인
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
